import { type ApiContainerType, type ApiService, EnumContainerActionRequest } from '@Model';
import { defineWizardStep, type ILinkInputProperties, type WizardStep } from '@synqo/frontend-component-library';
import { type ContainerActionRequest } from '@utils/requisitionAction';
import { type ContainerPosition, type ContainerStockLine } from '../../containerOrder';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import { type DetailsStepErrorKey, validateDetails } from './detailsErrors';
import { PlacementStepBody } from './PlacementStepBody';
import { type ContainerStockView, StockStepBody } from './StockStepBody';

interface DefineDetailsStepProps {
    /** The container action this step is for. Loose material and exchanges have steps of their own. */
    action: DetailsAction;
    stock: ContainerStockView;
    /** The container this step is about, undefined while no type has been named. */
    position: ContainerPosition | undefined;
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    contentField: ILinkInputProperties<ApiService>;
    /** The waste code of the chosen content, empty while none is known. */
    avvCode: string;
    onTakeLine: (line: ContainerStockLine) => void;
    onCount: (count: number) => void;
    onContainerNumber: (containerNumber: string) => void;
    onLocationHint: (locationHint: string) => void;
    onRemove: () => void;
    i18n: (key: string) => string;
}

export type DetailsAction = Exclude<ContainerActionRequest, EnumContainerActionRequest.Exchange>;

const STEP_LABELS = {
    [EnumContainerActionRequest.Place]: 'Stellen',
    [EnumContainerActionRequest.Collect]: 'Abholen',
    [EnumContainerActionRequest.Empty]: 'Leeren',
} as const;

/**
 * Step 3 of a container order: the stock selection or the placement form, depending on the
 * action taken.
 *
 * Both variants save every answer as it is given, so the step has nothing to confirm: "Weiter" is
 * what says it is done. It goes on once the order carries its container and that container
 * says what is in it — the content carries pricing and the waste code, so it cannot be left open.
 */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function defineDetailsStep({
    action,
    stock,
    position,
    containerTypeField,
    contentField,
    avvCode,
    onTakeLine,
    onCount,
    onContainerNumber,
    onLocationHint,
    onRemove,
    i18n,
}: DefineDetailsStepProps): WizardStep<RequisitionWizardState> {
    return defineWizardStep<RequisitionWizardState, DetailsStepErrorKey>({
        key: 'details',
        label: i18n(STEP_LABELS[action]),
        render: () =>
            action === EnumContainerActionRequest.Place ? (
                <PlacementStepBody
                    position={position}
                    containerTypeField={containerTypeField}
                    contentField={contentField}
                    avvCode={avvCode}
                    onCount={onCount}
                    onLocationHint={onLocationHint}
                />
            ) : (
                <StockStepBody
                    action={action}
                    stock={stock}
                    position={position}
                    containerTypeField={containerTypeField}
                    contentField={contentField}
                    avvCode={avvCode}
                    onTakeLine={onTakeLine}
                    onCount={onCount}
                    onContainerNumber={onContainerNumber}
                    onLocationHint={onLocationHint}
                    onRemove={onRemove}
                />
            ),
        validate: () =>
            validateDetails(
                position,
                action === EnumContainerActionRequest.Place
                    ? i18n('Bitte wähle einen Containertyp.')
                    : i18n('Bitte wähle einen Container aus oder beschreibe ihn.'),
                i18n('Bitte gib an, was in diesem Container ist.'),
            ),
    });
}
